import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Download, Share2, Check } from 'lucide-react';

/**
 * Photo gallery renderer with 3 layouts:
 *   - 'grid'     → uniform square thumbnails (object-cover)
 *   - 'mosaic'   → CSS columns masonry (natural aspect ratios)
 *   - 'carousel' → horizontal scroller with arrows
 *
 * Tap any image → lightbox modal with caption/description/date/role,
 * a "Download" button (saves the photo locally) and a "Share" button
 * (copies a direct URL with `?photo=<id>` so the recipient lands on the
 * gallery with the lightbox already open on that photo).
 *
 * Props:
 *   - items: GalleryPhoto[] (already sorted)
 *   - viewMode: 'grid' | 'mosaic' | 'carousel'
 *   - locale: 'es' | 'en'
 *   - testidPrefix: string (data-testid prefix for each item)
 *   - shareable: bool — when true, syncs ?photo=<id> in the URL and reads
 *     it on mount so a link opens the right lightbox (only meaningful on
 *     standalone gallery pages — pass `false` for embedded galleries).
 */
export default function PhotoGalleryView({ items, viewMode = 'grid', locale = 'es', testidPrefix = 'gallery', shareable = false }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  function caption(it) { return locale === 'en' ? (it.caption_en || it.caption_es) : (it.caption_es || it.caption_en); }
  function description(it) { return locale === 'en' ? (it.description_en || it.description_es) : (it.description_es || it.description_en); }

  // Sync ?photo=<id> ↔ open lightbox (one-way each direction to avoid loops).
  useEffect(() => {
    if (!shareable || !items || items.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const photoId = params.get('photo');
    if (!photoId) return;
    const idx = items.findIndex((it) => it.id === photoId);
    if (idx >= 0) setLightboxIdx(idx);
    // Intentionally only on mount-with-items.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareable, items?.length]);

  const openLightbox = useCallback((idx) => {
    setLightboxIdx(idx);
    if (shareable && items?.[idx]?.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('photo', items[idx].id);
      window.history.replaceState({}, '', url.toString());
    }
  }, [shareable, items]);

  const closeLightbox = useCallback(() => {
    setLightboxIdx(null);
    if (shareable) {
      const url = new URL(window.location.href);
      url.searchParams.delete('photo');
      window.history.replaceState({}, '', url.toString());
    }
  }, [shareable]);

  const stepLightbox = useCallback((delta) => {
    setLightboxIdx((cur) => {
      if (cur === null) return cur;
      const next = Math.min(items.length - 1, Math.max(0, cur + delta));
      if (shareable && items?.[next]?.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('photo', items[next].id);
        window.history.replaceState({}, '', url.toString());
      }
      return next;
    });
  }, [items, shareable]);

  if (!items || items.length === 0) {
    return (
      <div className="text-center text-charcoal-500 py-12 italic" data-testid={`${testidPrefix}-empty`}>
        {locale === 'es' ? 'Aún no hay fotos en esta galería.' : 'No photos in this gallery yet.'}
      </div>
    );
  }

  return (
    <>
      {viewMode === 'grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4" data-testid={`${testidPrefix}-grid`}>
          {items.map((it, idx) => (
            <button
              key={it.id}
              onClick={() => openLightbox(idx)}
              data-testid={`${testidPrefix}-item-${idx}`}
              className="group aspect-square overflow-hidden rounded-xl bg-charcoal-100 ring-1 ring-charcoal-100 hover:ring-cmic-300 transition-all duration-200 relative"
            >
              <img src={it.image_url} alt={caption(it) || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              {(caption(it) || it.role) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 text-left">
                  {caption(it) && <div className="text-white text-xs sm:text-sm font-semibold leading-tight line-clamp-2">{caption(it)}</div>}
                  {it.role && <div className="text-white/80 text-[10px] sm:text-xs mt-0.5">{it.role}</div>}
                  {it.date && <div className="text-white/70 text-[10px] sm:text-xs">{it.date}</div>}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {viewMode === 'mosaic' && (
        <div className="columns-2 sm:columns-3 md:columns-4 gap-3 sm:gap-4 [column-fill:_balance]" data-testid={`${testidPrefix}-mosaic`}>
          {items.map((it, idx) => (
            <button
              key={it.id}
              onClick={() => setLightboxIdx(idx)}
              data-testid={`${testidPrefix}-item-${idx}`}
              className="break-inside-avoid mb-3 sm:mb-4 w-full group relative overflow-hidden rounded-xl bg-charcoal-100 ring-1 ring-charcoal-100 hover:ring-cmic-300 transition-all duration-200 block"
            >
              <img src={it.image_url} alt={caption(it) || ''} className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-300" />
              {(caption(it) || it.role) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 text-left">
                  {caption(it) && <div className="text-white text-xs sm:text-sm font-semibold leading-tight line-clamp-2">{caption(it)}</div>}
                  {it.role && <div className="text-white/80 text-[10px] sm:text-xs mt-0.5">{it.role}</div>}
                  {it.date && <div className="text-white/70 text-[10px] sm:text-xs">{it.date}</div>}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {viewMode === 'carousel' && (
        <Carousel items={items} caption={caption} onOpen={openLightbox} testidPrefix={testidPrefix} />
      )}

      {lightboxIdx !== null && (
        <Lightbox
          item={items[lightboxIdx]}
          caption={caption(items[lightboxIdx])}
          description={description(items[lightboxIdx])}
          hasPrev={lightboxIdx > 0}
          hasNext={lightboxIdx < items.length - 1}
          onPrev={() => stepLightbox(-1)}
          onNext={() => stepLightbox(1)}
          onClose={closeLightbox}
          locale={locale}
          testidPrefix={testidPrefix}
          shareable={shareable}
        />
      )}
    </>
  );
}

function Carousel({ items, caption, onOpen, testidPrefix }) {
  const ref = useRef(null);
  function scroll(dir) {
    if (!ref.current) return;
    const card = ref.current.querySelector('[data-card]');
    const step = card ? card.offsetWidth + 16 : 320;
    ref.current.scrollBy({ left: dir * step, behavior: 'smooth' });
  }
  return (
    <div className="relative" data-testid={`${testidPrefix}-carousel`}>
      <button
        onClick={() => scroll(-1)}
        data-testid={`${testidPrefix}-carousel-prev`}
        aria-label="Anterior"
        className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white text-charcoal-700 shadow-lg ring-1 ring-charcoal-100 hover:bg-cmic-50 hover:text-cmic-700"
      >
        <ChevronLeft size={20} />
      </button>
      <div
        ref={ref}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 -mx-2 px-2 scrollbar-thin"
        style={{ scrollbarWidth: 'thin' }}
      >
        {items.map((it, idx) => (
          <button
            key={it.id}
            data-card
            onClick={() => onOpen(idx)}
            data-testid={`${testidPrefix}-item-${idx}`}
            className="snap-start flex-shrink-0 w-[260px] sm:w-[300px] group rounded-2xl bg-white ring-1 ring-charcoal-100 hover:ring-cmic-300 hover:shadow-md transition-all overflow-hidden text-left"
          >
            <div className="aspect-[4/5] overflow-hidden bg-charcoal-100">
              <img src={it.image_url} alt={caption(it) || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </div>
            {(caption(it) || it.role || it.date) && (
              <div className="px-4 py-3">
                {caption(it) && <div className="text-charcoal-800 font-semibold text-sm leading-tight line-clamp-2">{caption(it)}</div>}
                {it.role && <div className="text-cmic-600 text-xs font-medium mt-1 uppercase tracking-wider">{it.role}</div>}
                {it.date && <div className="text-charcoal-500 text-xs mt-0.5 tabular-nums">{it.date}</div>}
              </div>
            )}
          </button>
        ))}
      </div>
      <button
        onClick={() => scroll(1)}
        data-testid={`${testidPrefix}-carousel-next`}
        aria-label="Siguiente"
        className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white text-charcoal-700 shadow-lg ring-1 ring-charcoal-100 hover:bg-cmic-50 hover:text-cmic-700"
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
}

function Lightbox({ item, caption, description, hasPrev, hasNext, onPrev, onNext, onClose, locale, testidPrefix = 'gallery', shareable = false }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      else if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [hasPrev, hasNext, onClose, onPrev, onNext]);

  // Reset transient UI when the displayed item changes.
  useEffect(() => { setCopied(false); }, [item?.id]);

  async function handleDownload() {
    if (!item?.image_url || downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(item.image_url, { mode: 'cors' });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const slug = (caption || item.id || 'foto')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'foto';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch {
      // Fallback: open in new tab so the user can long-press → save.
      window.open(item.image_url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  }

  async function handleShare() {
    if (!item?.id) return;
    const url = new URL(window.location.href);
    url.searchParams.set('photo', item.id);
    const shareUrl = url.toString();
    const shareData = { title: caption || (locale === 'es' ? 'Foto CMIC' : 'CMIC Photo'), url: shareUrl };
    try {
      if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
        await navigator.share(shareData);
        return;
      }
    } catch { /* user cancelled, fall through to clipboard */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt(locale === 'es' ? 'Copia este enlace:' : 'Copy this link:', shareUrl);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" data-testid={`${testidPrefix}-lightbox`} onClick={onClose}>
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2 z-10" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={handleDownload}
          disabled={downloading}
          aria-label={locale === 'es' ? 'Descargar foto' : 'Download photo'}
          data-testid={`${testidPrefix}-lightbox-download`}
          className="h-10 px-3 sm:px-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs sm:text-sm font-medium disabled:opacity-50"
        >
          <Download size={15} />
          <span className="hidden sm:inline">{downloading ? (locale === 'es' ? 'Descargando…' : 'Downloading…') : (locale === 'es' ? 'Descargar' : 'Download')}</span>
        </button>
        {shareable && item?.id && (
          <button
            onClick={handleShare}
            aria-label={locale === 'es' ? 'Compartir foto' : 'Share photo'}
            data-testid={`${testidPrefix}-lightbox-share`}
            className={`h-10 px-3 sm:px-4 inline-flex items-center gap-1.5 rounded-full text-white text-xs sm:text-sm font-medium transition-colors ${
              copied ? 'bg-emerald-500/80 hover:bg-emerald-500/90' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            {copied ? <Check size={15} /> : <Share2 size={15} />}
            <span className="hidden sm:inline">
              {copied
                ? (locale === 'es' ? '¡Enlace copiado!' : 'Link copied!')
                : (locale === 'es' ? 'Compartir' : 'Share')}
            </span>
          </button>
        )}
        <button onClick={onClose} aria-label="Cerrar" data-testid={`${testidPrefix}-lightbox-close`} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white">
          <X size={22} />
        </button>
      </div>
      {hasPrev && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Anterior" data-testid={`${testidPrefix}-lightbox-prev`} className="absolute left-3 sm:left-6 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white">
          <ChevronLeft size={26} />
        </button>
      )}
      {hasNext && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Siguiente" data-testid={`${testidPrefix}-lightbox-next`} className="absolute right-3 sm:right-6 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white">
          <ChevronRight size={26} />
        </button>
      )}
      <div className="max-w-5xl max-h-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        <img src={item.image_url} alt={caption || ''} className="max-h-[70vh] max-w-full object-contain rounded-lg shadow-2xl" />
        {(caption || description || item.role || item.date) && (
          <div className="text-center text-white max-w-2xl px-4">
            {caption && <div className="text-lg sm:text-xl font-semibold">{caption}</div>}
            {(item.role || item.date) && (
              <div className="text-white/80 text-sm mt-1 flex items-center justify-center gap-3">
                {item.role && <span className="uppercase tracking-wider text-cmic-200">{item.role}</span>}
                {item.role && item.date && <span className="text-white/40">·</span>}
                {item.date && <span className="tabular-nums">{item.date}</span>}
              </div>
            )}
            {description && <p className="text-white/85 text-sm sm:text-base mt-3 leading-relaxed">{description}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
